import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ROUTES } from "../../config/links";
import ExerciseFeedback from "./ExerciseFeedback";
import ExerciseViewer from "./ExerciseViewer";
import "./exerciseHub.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUDIT_ID_RE = /^[A-Za-z0-9_-]{1,96}$/;

export default function ExerciseHub() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const auditId = useMemo(() => {
    const value = searchParams.get("auditId");
    return value && AUDIT_ID_RE.test(value) ? value : null;
  }, [searchParams]);
  const [exercise, setExercise] = useState(null);
  const [state, setState] = useState("loading");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewerReady, setViewerReady] = useState(false);

  const track = (eventType) => {
    if (!exercise) return;
    fetch(`${API_URL}/api/exercises/${exercise.slug}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventType, auditId }),
      keepalive: true,
    }).catch(() => {});
  };

  useEffect(() => {
    let active = true;
    setState("loading");
    fetch(`${API_URL}/api/exercises/${slug}/metadata`)
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((data) => {
        if (!active) return;
        setExercise(data);
        setViewerReady(false);
        setState("ready");
      })
      .catch(() => active && setState("missing"));
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (exercise) track("EXERCISE_HUB_VISIT");
  }, [exercise]);

  // Fallback: some environments (mobile browsers, PDF-blocking policies,
  // headless/embedded contexts) never fire the iframe's onLoad event, which
  // would otherwise leave the reflection gate permanently unmounted. If the
  // viewer hasn't signalled ready within a few seconds, unblock it anyway.
  useEffect(() => {
    if (!exercise || exercise.assetStatus !== "AVAILABLE") return;
    const timer = setTimeout(() => setViewerReady(true), 4000);
    return () => clearTimeout(timer);
  }, [exercise]);

  const printPdf = () => {
    if (!exercise) return;
    const popup = window.open(exercise.pdfAssetPath, "_blank", "noopener,noreferrer");
    if (popup) popup.addEventListener("load", () => popup.print(), { once: true });
  };

  if (state === "loading") return <main className="exercise-shell" data-testid="exercise-hub-loading">Loading exercise…</main>;
  if (state === "missing") return <main className="exercise-shell" data-testid="exercise-hub-not-found"><h1>Exercise not found</h1><Link to="/" data-testid="exercise-home-link">Return home</Link></main>;

  const title = exercise.title || "Exercise";
  const available = exercise.assetStatus === "AVAILABLE";
  const hub = (
    <main className="exercise-shell" data-testid="exercise-hub">
      <header className="exercise-header" data-testid="exercise-header"><Link to="/" data-testid="exercise-logo-link"><img src="/brand/exercise-hub-logo.png" alt="InterAct Globally" data-testid="exercise-logo-image" /></Link><span>Exercise Hub</span></header>
      <section className="exercise-intro">
        <p className="exercise-eyebrow">Practice material</p>
        <h1 data-testid="exercise-title">{title}</h1>
        {exercise.estimatedDurationMinutes && <span className="exercise-duration-badge" data-testid="exercise-duration-badge">{exercise.estimatedDurationMinutes} min</span>}
        {exercise.shortPurpose && <p className="exercise-purpose" data-testid="exercise-purpose">{exercise.shortPurpose}</p>}
        {(exercise.whenToUse || exercise.whenNotToUse) && (
          <div className="exercise-usage-guidance" data-testid="exercise-usage-guidance">
            {exercise.whenToUse && <div className="exercise-when-to-use" data-testid="exercise-when-to-use"><h3>When to use this</h3><p>{exercise.whenToUse}</p></div>}
            {exercise.whenNotToUse && <div className="exercise-when-not-to-use" data-testid="exercise-when-not-to-use"><h3>When not to use this</h3><p>{exercise.whenNotToUse}</p></div>}
          </div>
        )}
      </section>
      {available ? (
        <ExerciseViewer pdfAssetPath={exercise.pdfAssetPath} isFullscreen={false} onFullscreen={() => setIsFullscreen(true)} onExitFullscreen={() => setIsFullscreen(false)} onDownload={() => track("PDF_DOWNLOAD")} onPrint={printPdf} onReady={() => setViewerReady(true)} />
      ) : (
        <section className="exercise-coming-soon" data-testid="exercise-pdf-coming-soon"><p className="exercise-eyebrow">Exercise material</p><h2>Exercise material coming soon</h2><p>This permanent route is ready. The PDF will appear here once it is uploaded.</p></section>
      )}
      {available && viewerReady && <ExerciseFeedback apiUrl={API_URL} slug={exercise.slug} exerciseId={exercise.exerciseId} auditId={auditId} />}
      <section className="exercise-next-steps" data-testid="exercise-next-steps">
        <Link to={ROUTES.bookPresentation} onClick={() => track("CONSULTATION_CTA_CLICK")} data-testid="exercise-consultation-cta">Book your 1:1 Executive Consultation</Link>
        <Link to={ROUTES.highStakesPresentationCoaching} onClick={() => track("HIGH_STAKES_CTA_CLICK")} data-testid="exercise-high-stakes-cta">Explore High-Stakes Presentation Preparation</Link>
      </section>
    </main>
  );

  return <>{hub}{available && isFullscreen && <div className="exercise-focus-overlay" data-testid="exercise-fullscreen-overlay"><ExerciseViewer pdfAssetPath={exercise.pdfAssetPath} isFullscreen onFullscreen={() => {}} onExitFullscreen={() => setIsFullscreen(false)} onDownload={() => track("PDF_DOWNLOAD")} onPrint={printPdf} /></div>}</>;
}